/**
 * The deployment-stage cue (dev / preprod) — pure data, no DOM.
 *
 * The server classifies (internal/deployenv.Classify); this maps its canonical
 * answer onto what a tab shows. Prod maps to null: it is never marked.
 */

export type StageTone = 'success' | 'warning';

export interface StageCue {
  /** Canonical stage, [a-z0-9-]{1,16}, never `production` — e.g. `development`, `preprod`, `qa`. */
  stage: string;
  /** The tab-title code, [A-Z0-9-]{1,16} — `DEV`, `PRE`, or the stage uppercased. */
  code: string;
  tone: StageTone;
}

/**
 * Palette tokens (globals.css custom properties) per tone.
 * - `fill` backs the chip and the theme-colour meta.
 * - `text` is the chip label on that fill: 4.5:1 (WCAG AA, 11px text).
 * - `mark` is every small graphical mark — the stripe, the collapsed-rail dot,
 *   the favicon dot — which needs 3:1 on --background and --surface (WCAG 1.4.11).
 * Plain --warning is 2.09:1 on light --background, so warning marks use
 * --warning-strong; --success-foreground on --success is 2.05:1 in dark, so the
 * success label uses --background. __tests__/stageMarkerContrast.test.ts
 * re-derives every pair from globals.css on each run.
 */
export const TONE_TOKENS = {
  success: { fill: 'success', text: 'background', mark: 'success' },
  warning: { fill: 'warning', text: 'warning-foreground', mark: 'warning-strong' },
} as const satisfies Record<StageTone, { fill: string; text: string; mark: string }>;

/**
 * Per-origin cache of the last cue, applied before hydration by
 * STAGE_HINT_SCRIPT. NOT `lvdr_session_*`: it is machine-scoped, not user
 * data, and must survive logout — a logged-out dev tab is still dev.
 */
export const STAGE_HINT_KEY = 'lvdr_stage_hint';

/** Shared with STAGE_HINT_SCRIPT, which interpolates their `.source`. */
export const STAGE_RE = /^[a-z0-9-]{1,16}$/;
export const CODE_RE = /^[A-Z0-9-]{1,16}$/;
/**
 * Any stage prefix this module ever writes — so a stage switch never stacks
 * two. The space may be missing at the end: document.title strips trailing
 * whitespace, so on an empty <title> the prefix reads back as `[PRE]`.
 */
export const TITLE_PREFIX_RE = /^\[[A-Z0-9-]{1,16}\](?:\s|$)/;

/**
 * Maps the server's `environment` to a cue. '' (no answer, or a backend that
 * predates the field) and `production` are unmarked. An unrecognised stage IS
 * marked, in the caution tone — unmarked would claim prod.
 */
export function markerFor(environment: string): StageCue | null {
  const stage = environment.trim().toLowerCase();
  if (stage === '' || stage === 'production') return null;
  if (stage === 'development') return { stage, code: 'DEV', tone: 'success' };
  if (stage === 'preprod') return { stage, code: 'PRE', tone: 'warning' };
  if (!STAGE_RE.test(stage)) return { stage: 'unknown', code: 'UNKNOWN', tone: 'warning' };
  return { stage, code: stage.toUpperCase(), tone: 'warning' };
}

export function titlePrefix(cue: StageCue): string {
  return `[${cue.code}] `;
}

/** Shape check for a stored hint. A `production` hint is rejected: prod is never marked. */
export function isStageCue(v: unknown): v is StageCue {
  if (typeof v !== 'object' || v === null) return false;
  const c = v as Record<string, unknown>;
  return (
    typeof c.stage === 'string' &&
    STAGE_RE.test(c.stage) &&
    c.stage !== 'production' &&
    typeof c.code === 'string' &&
    CODE_RE.test(c.code) &&
    (c.tone === 'success' || c.tone === 'warning')
  );
}

export function readStageHint(): StageCue | null {
  try {
    const raw = window.localStorage.getItem(STAGE_HINT_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isStageCue(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Writes the cue, or removes the hint when `null` (the server said prod). */
export function writeStageHint(cue: StageCue | null): void {
  try {
    if (cue) window.localStorage.setItem(STAGE_HINT_KEY, JSON.stringify(cue));
    else window.localStorage.removeItem(STAGE_HINT_KEY);
  } catch {
    // Storage disabled / quota: the hint is an optimisation, never a requirement.
  }
}
